/**
 * lx-page-contacts —— 联系人页
 *
 * 空 → 空态引导；非空 → 真实列表 + 字母索引条 + 浮动新建按钮。
 *
 * 浮动按钮与空态按钮走同一条路（都发 ui:contact-editor-open），
 * 保证两种状态下新建体验一致。
 */

import { LxElement, define } from '@/core/base-element';
import { contactsRepo } from '@/data/repo.contacts';
import type { LxEmptyState } from './lx-empty-state';
import './lx-empty-state';
import './lx-contact-list';

export class LxPageContacts extends LxElement {
  private _count = 0;
  private _loading = true;

  protected styles(): string {
    return `
      :host {
        display: flex;
        flex-direction: column;
        width: 100%;
        min-height: 0;
        position: relative;
      }

      lx-contact-list,
      lx-empty-state {
        flex: 1 1 auto;
        min-height: 0;
      }

      /* 浮动新建按钮。位置固定右下，避开底部输入栏 */
      .fab {
        position: absolute;
        right: var(--sp-4);
        bottom: var(--sp-4);
        width: 52px;
        height: 52px;
        display: grid;
        place-items: center;
        border-radius: var(--radius-full);
        background: var(--c-primary);
        color: var(--c-primary-fg);
        box-shadow: var(--shadow-md);
        transition: transform var(--dur-fast) var(--ease-standard), opacity var(--dur-fast) var(--ease-standard);
        z-index: 3;
      }

      .fab:hover { opacity: .9; }
      .fab:active { transform: scale(.94); }

      .fab svg { width: 24px; height: 24px; fill: none; stroke: currentColor; stroke-width: 2; }
    `;
  }

  protected render(): string {
    if (this._loading) return '';
    return this._count > 0
      ? `
        <lx-contact-list></lx-contact-list>
        <button class="fab" type="button" data-new aria-label="新建联系人">
          <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" stroke-linecap="round"/></svg>
        </button>
      `
      : '<lx-empty-state></lx-empty-state>';
  }

  protected onMounted(): void {
    const empty = this.$('lx-empty-state') as LxEmptyState | null;
    if (empty) {
      empty.options = {
        title: '通讯录是空的',
        description: '联系人会真实写入本机数据库，不会上传到任何服务器。',
        actionLabel: '新建联系人',
        hint: '新建联系人 王二',
      };
      empty.addEventListener('empty-action', () => {
        this.emit('ui:contact-editor-open', {});
      });
    }

    this.$('[data-new]')?.addEventListener('click', () => {
      this.emit('ui:contact-editor-open', {});
    });
  }

  private async load(): Promise<void> {
    const count = await contactsRepo.count();
    const wasLoading = this._loading;

    this._loading = false;
    if (count === this._count && !wasLoading) return;

    this._count = count;
    this.update();
  }

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
    this.subscribe('contact:list-changed', () => void this.load());
  }
}

define('lx-page-contacts', LxPageContacts);
